/**
 * ZipCodeForm: formulaire Pays + Code postal → API Shopify prepare_shipping_rates + async_shipping_rates.
 * Affiche les tarifs de livraison et expose data-has-rates pour l'activation du checkout.
 */
if (!customElements.get("zip-code-form")) {
  const PREPARE_DELAY_MS = 1000;
  const ASYNC_RETRY_DELAY_MS = 800;
  const ASYNC_MAX_RETRIES = 15;

  class ZipCodeForm extends HTMLElement {
    constructor() {
      super();
      this.hasRates = false;
      this._abortController = null;
    }

    connectedCallback() {
      this.countrySelect = this.querySelector(".shipping-country");
      this.zipInput = this.querySelector(".shipping-zip");
      this.submitBtn = this.querySelector(".zip-code-form__submit");
      this.submitTextEl = this.querySelector(".zip-code-form__submit-text");
      this.loadingEl = this.querySelector(".zip-code-form__loading");
      this.resultsEl = this.closest(".cart__zip-code-form")?.querySelector(".zip-code-form__label");
      this.errorMessage = this.querySelector(".error-message");
      this.toggleBtn = this.closest(".cart__zip-code-form")?.querySelector(".zip-code-form__toggle");

      if (!this.countrySelect || !this.zipInput || !this.submitBtn) return;

      this._setDefaultCountry();
      this._updateSubmitButton();
      this.submitBtn.addEventListener("click", this._onSubmit.bind(this));
      this.countrySelect.addEventListener("change", this._updateSubmitButton.bind(this));
      this.zipInput.addEventListener("input", this._updateSubmitButton.bind(this));

      if (this.toggleBtn) {
        this.toggleBtn.addEventListener("click", this._onToggleForm.bind(this));
      }
    }

    _onToggleForm() {
      this.style.display = "";
      if (this.toggleBtn) this.toggleBtn.style.display = "none";
    }

    _setFormVisible(visible) {
      this.style.display = visible ? "" : "none";
      if (this.toggleBtn) this.toggleBtn.style.display = visible ? "none" : "inline";
    }

    _canSubmit() {
      const zip = (this.zipInput?.value || "").trim();
      const country = (this.countrySelect?.value || "").trim();
      return zip.length > 0 && country.length > 0 && country !== "---";
    }

    _updateSubmitButton() {
      if (!this.submitBtn || this.submitBtn.classList.contains("loading")) return;
      this.submitBtn.disabled = !this._canSubmit();
    }

    _setDefaultCountry() {
      const def = this.countrySelect.getAttribute("data-default");
      if (def && this.countrySelect.querySelector(`option[value="${def}"]`)) {
        this.countrySelect.value = def;
      }
    }

    _getCartUrl() {
      return typeof window.theme !== "undefined" && window.theme.routes && window.theme.routes.cart_url ? window.theme.routes.cart_url : "/cart";
    }

    _getMoneyFormat() {
      if (typeof window.theme !== "undefined" && window.theme.settings) {
        return window.theme.settings.money_with_currency_format || window.theme.settings.money_format;
      }
      return "{{amount}}";
    }

    _params() {
      const zip = (this.zipInput.value || "").trim();
      const country = (this.countrySelect.value || "").trim();
      return {
        "shipping_address[zip]": zip,
        "shipping_address[country]": country,
      };
    }

    _setState(hasRates) {
      this.hasRates = hasRates;
      this.setAttribute("data-has-rates", hasRates ? "true" : "false");
      this.dispatchEvent(new CustomEvent("zip-code-form-update", { bubbles: true, detail: { hasRates } }));
    }

    _setLoading(loading) {
      if (this.loadingEl) this.loadingEl.style.display = loading ? "inline-flex" : "none";
      if (this.submitTextEl) this.submitTextEl.style.display = loading ? "none" : "";
      if (this.submitBtn) {
        this.submitBtn.disabled = loading ? true : !this._canSubmit();
        this.submitBtn.classList.toggle("loading", loading);
      }
    }

    _showError(show) {
      if (this.errorMessage) this.errorMessage.style.display = show ? "flex" : "none";
      if (this.zipInput) this.zipInput.classList.toggle("error", show);
    }

    _showResults(html) {
      if (!this.resultsEl) return;
      const defaultLabel = this.resultsEl.getAttribute("data-default-label") || "";
      if (html !== "" && html.indexOf("<") === -1) {
        this.resultsEl.textContent = html;
      } else {
        this.resultsEl.innerHTML = html !== "" ? html : defaultLabel;
      }
    }

    async _onSubmit() {
      const zip = (this.zipInput.value || "").trim();
      const country = (this.countrySelect.value || "").trim();

      if (!zip || !country || country === "---") {
        this._showError(true);
        this._showResults("");
        this._setState(false);
        return;
      }

      this._showError(false);
      this._showResults("");
      this._setState(false);
      this._setLoading(true);

      try {
        await this._runEstimation();
      } finally {
        this._setLoading(false);
      }
    }

    async _runEstimation() {
      if (this._abortController) this._abortController.abort();
      this._abortController = new AbortController();
      const signal = this._abortController.signal;

      const params = this._params();

      try {
        const queryString = new URLSearchParams(params).toString();
        const baseUrl = this._getCartUrl();

        const prepareRes = await fetch(`${baseUrl}/prepare_shipping_rates.json?${queryString}`, {
          method: "POST",
          signal,
        });

        if (!prepareRes.ok) {
          this._showError(true);
          this._showResults(this.resultsEl?.getAttribute("data-default-label") || "");
          this._setState(false);
          return;
        }

        await new Promise((resolve) => setTimeout(resolve, PREPARE_DELAY_MS));
        if (signal.aborted) return;

        const rates = await this._fetchAsyncRates(queryString, baseUrl, signal);
        if (signal.aborted) return;

        if (rates && Array.isArray(rates) && rates.length > 0) {
          const format = this._getMoneyFormat() || "{{amount}}";
          const r = rates[0];
          const priceCents = Math.round(parseFloat(r.price || 0) * 100);
          const formatted = typeof formatMoney === "function" ? formatMoney(priceCents, format) : r.price + " " + (r.currency || "");
          const line = formatted + " TTC";
          if (this.resultsEl) this.resultsEl.classList.remove("text-sm");
          this._showResults(line);
          this._setState(true);
          this._setFormVisible(false);
        } else {
          this._showError(true);
          this._showResults(this.resultsEl?.getAttribute("data-default-label") || "");
          this._setState(false);
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        this._showError(true);
        this._showResults(this.resultsEl?.getAttribute("data-default-label") || "");
        this._setState(false);
      }
    }

    async _fetchAsyncRates(queryString, baseUrl, signal, attempt = 0) {
      const res = await fetch(`${baseUrl}/async_shipping_rates.json?${queryString}`, { signal });
      const text = await res.text();

      if (!res.ok) {
        throw new Error("Erreur async_shipping_rates: HTTP " + res.status);
      }

      if (text === "null" || text.trim() === "") {
        if (attempt >= ASYNC_MAX_RETRIES) {
          return [];
        }
        await new Promise((r) => setTimeout(r, ASYNC_RETRY_DELAY_MS));
        if (signal.aborted) return null;
        return this._fetchAsyncRates(queryString, baseUrl, signal, attempt + 1);
      }

      let data;
      try {
        data = JSON.parse(text);
      } catch (_) {
        return [];
      }

      return data && data.shipping_rates ? data.shipping_rates : [];
    }
  }

  customElements.define("zip-code-form", ZipCodeForm);
}
